import { Divider, MenuList, Stack, Typography, useTheme } from '@mui/material'
import { ISubCategory } from 'api/types/category'
import { NavBarSubItem } from './nav-bar-sub-item'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  categoryId: string
  subCategories: Array<ISubCategory>
  onClose: () => void
}

const ROWS_PER_COLUMN = 7

const gridSx = {
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '175px',
  gridTemplateRows: 'repeat(auto-fit, minmax(29px, 1fr))',
  maxHeight: '210px',
  padding: 0,
  [`& > :nth-last-child(-n+${ROWS_PER_COLUMN})`]: {
    borderRight: 'none',
  },
} as const

export const NavBarSubList = ({ categoryId, subCategories, onClose }: Props) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const headerSx = {
    fontWeight: 700,
    color: theme.palette.primary.main,
  }

  const { regular, herbs } = useMemo(() => {
    const regular: Array<ISubCategory> = []
    const herbs: Array<ISubCategory> = []

    for (const sub of subCategories) {
      if (sub.type === 'herbs') {
        herbs.push(sub)
      } else {
        regular.push(sub)
      }
    }

    return { regular, herbs }
  }, [subCategories])

  if (!subCategories || subCategories.length === 0) {
    return null
  }

  const hasHerbs = herbs.length > 0

  const renderItem = (subCategory: ISubCategory) => (
    <NavBarSubItem
      key={subCategory.id}
      categoryId={categoryId}
      subCategory={subCategory}
      onClose={onClose}
    />
  )

  return (
    <MenuList
      sx={{
        position: 'absolute',
        top: '22px',
        left: '-1px',
        minWidth: '170px',
        padding: '12px 0 9px 3px',
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.primary.main}`,
        borderTopRightRadius: theme.shape.borderRadius,
        zIndex: 1000,
        boxShadow:
          '4px 5px 5px -3px rgba(0, 0, 0, 0.2), 9px 8px 10px 1px rgba(0, 0, 0, 0.14), 4px 3px 14px 2px rgba(0, 0, 0, 0.12)',
      }}
    >
      {hasHerbs ? (
        <>
          <MenuList sx={gridSx}>{regular.map(renderItem)}</MenuList>
          <Divider sx={{ my: 1, borderColor: theme.palette.primary.main }} />
          <Stack spacing={0.5}>
            <Typography sx={headerSx}>{t('herbs')}</Typography>
            <MenuList sx={gridSx}>{herbs.map(renderItem)}</MenuList>
          </Stack>
        </>
      ) : (
        <MenuList sx={gridSx}>{regular.map(renderItem)}</MenuList>
      )}
    </MenuList>
  )
}
